// SPDX-License-Identifier: MIT
/**
 * ui-dashboard (proof, 5–8 beats). The product's own dashboard makes the case: a browser window
 * rises into frame with the brand in its sidebar, one to three metric tiles roll in to brand facts
 * (the first lands on beat 2 and a light sweeps across it), a chart grows from facts that share a
 * unit (or a ring fills to a rate), and a notification can slide in at the end. The camera pushes
 * slowly towards the first metric.
 *
 * Only facts are plotted. Labels are claims; navigation, headers and list entries without a source
 * are skeleton bars.
 */
import * as z from "zod/mini";
import { font } from '../brand.ts';
import { clamp, lerp } from '../core/math.ts';
import * as kit from '../kit/index.ts';
import type { Box } from '../kit/path.ts';
import { DESIGN_H, DESIGN_W, defineScene } from '../scene.ts';
import { logoHasWordmark, markBox, sceneBeats } from './lib.ts';
const { ui } = kit;
const FRAME: Box = { x: 170, y: 96, w: DESIGN_W - 340, h: DESIGN_H - 192 };
const SIDE = 270;
const PAD = 40;
const GAP = 28;
/** When the metric tiles start rolling, one after another. */
const KPI_AT = 0.7;
const KPI_STEP = 0.45;
const KPI_ROLL = 1.3;
/** Where tile i's roll starts (as it comes in) and where its value reads as landed. */
const rollAt = (i: number) => KPI_AT + i * KPI_STEP - 0.3;
const landAt = (i: number) => rollAt(i) + (KPI_ROLL + 0.3) * 0.85;
const Percent = z
    .string().check(z.maxLength(16)).check(z.regex(/^\d+(?:\.\d+)?\s?%$/, 'a ring shows a percentage, e.g. "99.2%"'));
const propsSchema = z.object({
    kpis: z
        .array(z.object({ value: z.string().check(z.maxLength(16)), label: z.string().check(z.maxLength(40)) })).check(z.minLength(1)).check(z.maxLength(3)),
    chart: z.optional(z
        .discriminatedUnion('kind', [
        z.object({
            kind: z.literal('bars'),
            /** Facts that share a unit; heights follow their values. */
            items: z
                .array(z.object({ label: z.string().check(z.maxLength(20)), value: z.string().check(z.maxLength(16)) })).check(z.minLength(2)).check(z.maxLength(4)),
        }),
        z.object({ kind: z.literal('ring'), value: Percent, label: z.string().check(z.maxLength(40)) }),
    ])),
    toast: z.optional(z.string().check(z.maxLength(40))),
    url: z.optional(z.string().check(z.maxLength(40))),
});
/** The notification arrives in time to be read before the scene ends. */
const toastAt = (beats: number, spb: number, text: string) => Math.max(KPI_AT + 2, beats - Math.max(2, kit.text.readingTime(text) / spb + 0.6));
export default defineScene({
    id: 'ui-dashboard',
    version: '1.0.0',
    aspects: ['16:9'],
    propsSchema,
    glyphs: (p) => [
        ...p.kpis.flatMap((k) => [k.value, k.label]),
        ...(p.chart?.kind === 'bars' ? p.chart.items.flatMap((i) => [i.label, i.value]) : []),
        ...(p.chart?.kind === 'ring' ? [p.chart.value, p.chart.label] : []),
        p.toast ?? '',
        p.url ?? '',
        '0123456789',
    ].join(''),
    claims: (p) => [
        ...p.kpis.map((k) => k.label),
        ...(p.chart?.kind === 'bars' ? p.chart.items.map((i) => i.label) : []),
        ...(p.chart?.kind === 'ring' ? [p.chart.label] : []),
        ...(p.toast ? [p.toast] : []),
    ],
    motion: (_p, env) => (env.localBeat < KPI_AT + 2 ? 0.45 : 0.2),
    audioEvents: (p, { beats, spb }) => [
        // A blip as each metric lands, and one for the notification.
        ...p.kpis.map((_, i) => ({
            beat: landAt(i),
            sfx: 'blip' as const,
            gain: 0.45,
        })),
        ...(p.toast ? [{ beat: toastAt(beats, spb, p.toast), sfx: 'blip' as const, gain: 0.5 }] : []),
    ],
    post: (_p, env) => (env.style.background === 'dark' ? { bloom: 0.3, lens: 0 } : { bloom: 0.2, lens: 0 }),
    draw(ctx, props, env) {
        const tk = ui.tokens(env);
        const L = sceneBeats(env);
        const brand = env.brand;
        ctx.fillStyle = tk.bg;
        ctx.fillRect(0, 0, DESIGN_W, DESIGN_H);
        if (tk.dark)
            kit.fx.pool(ctx, DESIGN_W / 2, DESIGN_H * 0.45, 1100, tk.accent, 0.06);
        // --- Layout -----------------------------------------------------------------------------------
        // Below the browser bar (64) and its hairline, as kit.ui.browser lays it out.
        const content: Box = { x: FRAME.x, y: FRAME.y + 65.5, w: FRAME.w, h: FRAME.h - 65.5 };
        const main: Box = {
            x: content.x + SIDE + PAD,
            y: content.y + PAD,
            w: content.w - SIDE - 2 * PAD,
            h: content.h - 2 * PAD,
        };
        const n = props.kpis.length;
        const kpiH = 210;
        const kpiW = (main.w - GAP * (n - 1)) / n;
        const kpiBox = (i: number): Box => ({ x: main.x + i * (kpiW + GAP), y: main.y + 72, w: kpiW, h: kpiH });
        const lowerY = main.y + 72 + kpiH + GAP;
        const lowerH = main.y + main.h - lowerY;
        const chartW = props.chart ? Math.round(main.w * 0.6) : 0;
        const chartBox: Box = { x: main.x, y: lowerY, w: chartW, h: lowerH };
        const listBox: Box = {
            x: main.x + (chartW ? chartW + GAP : 0),
            y: lowerY,
            w: main.w - (chartW ? chartW + GAP : 0),
            h: lowerH,
        };
        // --- Camera: the window rises in, then a slow push towards the first metric ------------------
        const rise = kit.beat.seg(env, 0, 0.8, kit.curves.glide);
        const k0 = kpiBox(0);
        const push = kit.beat.seg(env, 0.4, L, kit.curves.silk);
        kit.camera.zoom(ctx, lerp(0.96, 1.05, push), lerp(DESIGN_W / 2, k0.x + k0.w / 2, 0.5), lerp(DESIGN_H / 2, k0.y, 0.5));
        ctx.save();
        ctx.globalAlpha *= clamp(rise * 1.4);
        ctx.translate(0, (1 - rise) * 80);
        ui.browser(ctx, tk, FRAME, props.url ? { url: props.url } : {});
        ctx.save();
        ui.rrect(ctx, content, tk.radius.lg);
        ctx.clip();
        // --- Sidebar: the brand, then skeleton navigation ------------------------------------------------
        ctx.fillStyle = tk.sunken;
        ctx.fillRect(content.x, content.y, SIDE, content.h);
        ctx.fillStyle = tk.border;
        ctx.fillRect(content.x + SIDE, content.y, 1.5, content.h);
        const side = kit.beat.seg(env, 0.3, 1, kit.curves.glide);
        ctx.save();
        ctx.globalAlpha *= side;
        const logo = brand.logo;
        const markB = logo
            ? markBox(brand, 0, 0, logoHasWordmark(brand) ? SIDE - 2 * PAD : 44, 44)
            : { x: 0, y: 0, w: 0, h: 0 };
        const mx = content.x + PAD;
        const my = content.y + PAD;
        if (logo) {
            const b = { ...markB, x: mx, y: my + (44 - markB.h) / 2 };
            for (const part of logo.parts) {
                ctx.fillStyle = part.fill ?? tk.accent;
                kit.path.tracePath(ctx, kit.path.fit(part.subpaths, b, logo.bounds));
                ctx.fill(part.fillRule);
            }
            env.report?.element('logo', [b.x, b.y, b.w, b.h]);
        }
        if (!logo || !logoHasWordmark(brand)) {
            const x = logo ? mx + markB.w + 14 : mx;
            ui.fitLabel(ctx, tk, brand.name, content.x + SIDE - PAD - x, 30, 700);
            ui.label(ctx, brand.name, x, my + 23, tk.text);
            if (!logo)
                env.report?.element('logo', [x, my + 8, ctx.measureText(brand.name).width, 30]);
        }
        for (let i = 0; i < 5; i++) {
            const y = my + 96 + i * 64;
            if (i === 0) {
                ui.rrect(ctx, { x: content.x + 20, y: y - 26, w: SIDE - 40, h: 52 }, tk.radius.md);
                ctx.fillStyle = tk.accentSoft;
                ctx.fill();
            }
            ctx.beginPath();
            ctx.arc(mx + 12, y, 10, 0, Math.PI * 2);
            ctx.fillStyle = i === 0 ? tk.accent : tk.skeleton;
            ctx.fill();
            ui.skeleton(ctx, tk, mx + 36, y - 6, (SIDE - 2 * PAD - 40) * (0.75 - 0.08 * (i % 3)), 12);
        }
        ctx.restore();
        // --- Heading: the brand's name; a skeleton line for the rest ------------------------------------
        ctx.save();
        ctx.globalAlpha *= side;
        ctx.font = font(brand, 700, 40);
        ctx.fillStyle = tk.text;
        ctx.textBaseline = 'middle';
        ctx.textAlign = 'left';
        ctx.fillText(brand.name, main.x, main.y + 22);
        ui.skeleton(ctx, tk, main.x + main.w - 260, main.y + 14, 260, 16, 0.8);
        ctx.restore();
        // --- Metric tiles roll in, the first one catches the light --------------------------------------
        // One value size for the row, so the tiles read as a set.
        const valuePx = Math.min(...props.kpis.map((k, i) => ui.kpiValuePx(ctx, tk, kpiBox(i), k.value)));
        props.kpis.forEach((k, i) => {
            const b = kpiBox(i);
            const t0 = KPI_AT + i * KPI_STEP;
            const enter = kit.beat.seg(env, t0 - 0.3, t0 + 0.2, kit.curves.glide);
            if (enter <= 0)
                return;
            ctx.save();
            ctx.globalAlpha *= enter;
            ctx.translate(0, (1 - enter) * 24);
            // The roll starts as the tile comes in, so no row of zeros sits waiting.
            ui.kpi(ctx, tk, b, k.label, k.value, kit.beat.seg(env, rollAt(i), t0 + KPI_ROLL), valuePx);
            ctx.restore();
        });
        const sweep = kit.beat.seg(env, landAt(0), landAt(0) + 0.7);
        if (sweep > 0 && sweep < 1)
            kit.fx.sweep(ctx, (l) => {
                ui.rrect(l, k0, tk.radius.lg);
                l.fill();
            }, k0, sweep, {
                width: 140,
                intensity: tk.dark ? 0.16 : 0.35,
                color: tk.dark ? '#FFFFFF' : kit.color.mix(tk.accent, '#FFFFFF', 0.7),
            });
        // --- Chart from facts ---------------------------------------------------------------------------
        const chartAt = KPI_AT + n * KPI_STEP + 0.3;
        const lower = kit.beat.seg(env, chartAt - 0.4, chartAt + 0.2, kit.curves.glide);
        if (props.chart && lower > 0) {
            ctx.save();
            ctx.globalAlpha *= lower;
            ui.panel(ctx, tk, chartBox, { lift: 1 });
            ui.skeleton(ctx, tk, chartBox.x + 28, chartBox.y + 30, chartBox.w * 0.28, 14);
            const grow = kit.beat.seg(env, chartAt, chartAt + 1.6);
            if (props.chart.kind === 'bars') {
                const values = props.chart.items.map((it) => kit.text.parseStat(it.value)?.n ?? 0);
                ui.bars(ctx, tk, { x: chartBox.x + 28, y: chartBox.y + 64, w: chartBox.w - 56, h: chartBox.h - 84 }, props.chart.items, grow, values.indexOf(Math.max(...values)));
            }
            else {
                const r = Math.min(chartBox.h * 0.32, 120);
                const cx = chartBox.x + 40 + r + 20;
                const cy = chartBox.y + chartBox.h / 2 + 18;
                const rate = (kit.text.parseStat(props.chart.value)?.n ?? 0) / 100;
                ui.ring(ctx, tk, cx, cy, r, rate * kit.curves.glide(grow), r * 0.18);
                ctx.save();
                ctx.globalAlpha *= clamp(grow * 3);
                ctx.font = tk.font(Math.round(r * 0.42), 700);
                ctx.fillStyle = tk.text;
                const w = ui.odometerWidth(ctx, props.chart.value);
                ui.odometer(ctx, props.chart.value, grow, cx - w / 2, cy + r * 0.15);
                ctx.restore();
                // The rate's label wraps beside the ring (it is a claim and is shown whole).
                const lx = cx + r + 48;
                ctx.font = tk.font(tk.type.title - 4, 600);
                const lines = kit.text.wrap(ctx, props.chart.label, chartBox.x + chartBox.w - 36 - lx);
                lines.forEach((l, i) => ui.label(ctx, l.text, lx, cy + (i - (lines.length - 1) / 2) * 44, tk.text));
            }
            ctx.restore();
        }
        // --- A list without invented entries ------------------------------------------------------------
        if (lower > 0) {
            ctx.save();
            ctx.globalAlpha *= lower;
            ui.panel(ctx, tk, listBox, { lift: 1 });
            ui.skeleton(ctx, tk, listBox.x + 28, listBox.y + 30, listBox.w * 0.4, 14);
            const rows = Math.max(1, Math.floor((listBox.h - 70) / 84));
            for (let i = 0; i < rows; i++) {
                const k = kit.beat.seg(env, chartAt + 0.2 + i * 0.15, chartAt + 0.6 + i * 0.15, kit.curves.glide);
                ui.row(ctx, tk, { x: listBox.x + 12, y: listBox.y + 64 + i * 84, w: listBox.w - 24, h: 76 }, { avatar: i, bars: [0.66 - 0.1 * (i % 3), 0.38], reveal: k });
            }
            ctx.restore();
        }
        ctx.restore(); // content clip
        // --- The notification ---------------------------------------------------------------------------
        if (props.toast) {
            const t0 = toastAt(L, env.spb, props.toast);
            const enter = kit.curves.spring(kit.beat.seg(env, t0 - 0.15, t0 + 0.55));
            ctx.font = tk.font(tk.type.body, 600);
            const w = clamp(ctx.measureText(props.toast).width + 160, 420, 760);
            ui.toast(ctx, tk, { x: FRAME.x + FRAME.w - w - 36, y: FRAME.y + FRAME.h - 88 - 36, w, h: 88 }, props.toast, {
                enter,
            });
        }
        ctx.restore(); // rise
    },
});
